
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AnalyticsParams {
  campaignId?: string;
  startDate?: string;
  endDate?: string;
  subscriberId?: string;
}

// Define proper types for the data returned by the queries
interface DeliveryStats {
  total: number;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  failed: number;
}

interface ClickData {
  url: string;
  count: number;
}

interface OpenRateData {
  date: string;
  total: number;
  opens: number;
  clicks: number;
  open_rate: number;
  click_rate: number;
}

export const useEmailAnalytics = ({ campaignId, startDate, endDate, subscriberId }: AnalyticsParams = {}) => {
  // Build the base query for filters
  const buildBaseQuery = (query: any) => {
    if (campaignId) {
      query = query.eq('campaign_id', campaignId);
    }
    if (startDate) {
      query = query.gte('sent_at', startDate);
    }
    if (endDate) {
      query = query.lte('sent_at', endDate);
    }
    if (subscriberId) {
      query = query.eq('subscriber_id', subscriberId);
    }
    return query;
  };

  // Get overall delivery stats
  const useDeliveryStats = () => {
    return useQuery({
      queryKey: ['email_delivery_stats', { campaignId, startDate, endDate, subscriberId }],
      queryFn: async () => {
        try {
          // Get total sent count
          let query = supabase
            .from('newsletter_sends')
            .select('*', { count: 'exact' });
            
          query = buildBaseQuery(query);
          
          const { count: total } = await query;
          
          // Get counts by status
          const statusCounts: Record<string, number> = {};
          
          // Status counts: delivered, bounced, failed
          const statuses = ['delivered', 'bounced', 'failed', 'sent', 'pending'];
          for (const status of statuses) {
            const { count } = await buildBaseQuery(
              supabase.from('newsletter_sends').select('*', { count: 'exact' })
            ).eq('status', status);
            
            statusCounts[status] = count || 0;
          }
          
          // Get open count
          const { count: opened } = await buildBaseQuery(
            supabase.from('newsletter_sends').select('*', { count: 'exact' })
          ).not('opened_at', 'is', null);
          
          // Get click count (unique sends with clicks)
          const { count: clicked } = await supabase
            .from('newsletter_clicks')
            .select('send_id', { count: 'exact', head: true })
            .in('send_id', 
              buildBaseQuery(supabase.from('newsletter_sends').select('id'))
            );
          
          // Format the data for easier consumption
          const stats: DeliveryStats = {
            total: total || 0,
            sent: statusCounts.sent || 0,
            delivered: statusCounts.delivered || 0,
            opened: opened || 0,
            clicked: clicked || 0,
            bounced: statusCounts.bounced || 0,
            complained: 0, // Not currently tracked
            failed: statusCounts.failed || 0
          };
          
          return stats;
        } catch (error) {
          console.error('Error fetching delivery stats:', error);
          toast.error('Failed to load delivery statistics');
          // Return empty stats object
          return {
            total: 0,
            sent: 0,
            delivered: 0,
            opened: 0,
            clicked: 0,
            bounced: 0,
            complained: 0,
            failed: 0
          };
        }
      }
    });
  };

  // Get open rate over time
  const useOpenRateOverTime = (interval: 'day' | 'week' | 'month' = 'day') => {
    return useQuery<OpenRateData[]>({
      queryKey: ['open_rate_over_time', { campaignId, startDate, endDate, subscriberId, interval }],
      queryFn: async () => {
        try {
          // Call the database function to get open rate data
          const { data, error } = await supabase.rpc('get_open_rate_over_time', {
            p_campaign_id: campaignId || null,
            p_start_date: startDate || null,
            p_end_date: endDate || null,
            p_interval: interval
          });
          
          if (error) throw error;
          
          // Map database results to our interface
          return (data || []).map(item => ({
            date: item.date,
            total: item.total,
            opens: item.opens,
            clicks: item.clicks,
            open_rate: item.open_rate,
            click_rate: item.click_rate
          })) as OpenRateData[];
        } catch (error) {
          console.error('Error fetching open rate data:', error);
          toast.error('Failed to load open rate analytics');
          
          // Fallback to mock data for development
          if (process.env.NODE_ENV === 'development') {
            return generateMockOpenRateData(interval, startDate, endDate);
          }
          
          return [];
        }
      }
    });
  };

  // Mock data generator for open rate (used as fallback in development)
  const generateMockOpenRateData = (interval: string, startDate?: string, endDate?: string): OpenRateData[] => {
    const result: OpenRateData[] = [];
    const start = startDate ? new Date(startDate) : new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    const end = endDate ? new Date(endDate) : new Date();
    
    let current = new Date(start);
    while (current <= end) {
      const total = Math.floor(Math.random() * 100) + 50;
      const opens = Math.floor(Math.random() * total);
      const clicks = Math.floor(Math.random() * opens);
      
      result.push({
        date: current.toISOString().split('T')[0],
        total,
        opens,
        clicks,
        open_rate: parseFloat(((opens / total) * 100).toFixed(2)),
        click_rate: parseFloat(((clicks / (opens || 1)) * 100).toFixed(2))
      });
      
      // Increment date based on interval
      switch (interval) {
        case 'day':
          current.setDate(current.getDate() + 1);
          break;
        case 'week':
          current.setDate(current.getDate() + 7);
          break;
        case 'month':
          current.setMonth(current.getMonth() + 1);
          break;
        default:
          current.setDate(current.getDate() + 1);
      }
    }
    
    return result;
  };

  // Get click data
  const useClickData = () => {
    return useQuery<ClickData[]>({
      queryKey: ['email_click_data', { campaignId, startDate, endDate, subscriberId }],
      queryFn: async () => {
        try {
          // Get IDs of sends that match our criteria
          const {data: sendIds} = await buildBaseQuery(
            supabase.from('newsletter_sends').select('id')
          );
          
          if (!sendIds || sendIds.length === 0) {
            return [];
          }
          
          // Convert to array of IDs
          const sendIdValues = sendIds.map(item => item.id);
          
          // Get unique URLs clicked and their counts
          // Instead of using groupBy, we'll use a query with a database function or we can
          // process the data client-side if needed
          const { data, error } = await supabase
            .from('newsletter_clicks')
            .select('link_url, id')
            .in('send_id', sendIdValues);
          
          if (error) throw error;
          
          // Process data client-side to count occurrences of each URL
          const urlCounts: Record<string, number> = {};
          (data || []).forEach(item => {
            urlCounts[item.link_url] = (urlCounts[item.link_url] || 0) + 1;
          });
          
          // Format data for the frontend
          return Object.entries(urlCounts).map(([url, count]) => ({
            url,
            count
          })).sort((a, b) => b.count - a.count);
        } catch (error) {
          console.error('Error fetching click data:', error);
          toast.error('Failed to load click analytics');
          
          // Fallback to mock data for development
          if (process.env.NODE_ENV === 'development') {
            return [
              { url: 'https://example.com/product', count: 145 },
              { url: 'https://example.com/blog/email-marketing-tips', count: 98 },
              { url: 'https://example.com/pricing', count: 76 },
              { url: 'https://example.com/contact', count: 52 },
              { url: 'https://example.com/about', count: 34 },
            ];
          }
          
          return [];
        }
      }
    });
  };

  // Get geographic data
  const useGeographicData = () => {
    return useQuery({
      queryKey: ['email_geographic_data', { campaignId, startDate, endDate, subscriberId }],
      queryFn: async () => {
        // This would use IP geolocation data stored with open/click events
        // For now, we'll return a placeholder
        return [];
      }
    });
  };

  // Get device data
  const useDeviceData = () => {
    return useQuery({
      queryKey: ['email_device_data', { campaignId, startDate, endDate, subscriberId }],
      queryFn: async () => {
        // This would parse user agent strings from open/click events
        // For now, we'll return a placeholder
        return [];
      }
    });
  };

  return {
    useDeliveryStats,
    useOpenRateOverTime,
    useClickData,
    useGeographicData,
    useDeviceData
  };
};
